import {
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../api/services/contract/models";

/** Roles a settlement pays out to — the ones a cash-out entry is built for. */
const SETTLEMENT_PARTY_ROLES = [
  ESenderRole.CUSTOMER,
  ESenderRole.PROVIDER,
  ESenderRole.BROKER,
];

export type PartyIdentifierKey = "id" | "userId" | "userIdentification";

const IDENTIFIER_KEYS: PartyIdentifierKey[] = [
  "id",
  "userId",
  "userIdentification",
];

export interface IUnregisteredParty {
  role: ESenderRole;
  name?: string;
  phoneNumber?: string;
}

interface ISettlementParty extends IUnregisteredParty {
  identifiers: Partial<Record<PartyIdentifierKey, string | number | null>>;
}

const isPresent = (value?: string | number | null): boolean =>
  value !== undefined && value !== null && String(value).trim() !== "";

/**
 * A settlement can only be sent for a party the backend can resolve to a real
 * user, so anything that is not a usable identifier — 0, null, undefined, or
 * their stringified forms — means the party is not registered yet.
 */
export const isValidPartyIdentifier = (
  value?: string | number | null
): boolean => {
  if (!isPresent(value)) return false;

  const normalized = String(value).trim().toLowerCase();
  if (
    normalized === "null" ||
    normalized === "undefined" ||
    normalized === "nan"
  ) {
    return false;
  }

  const numeric = Number(normalized);
  // ids are positive numbers; a non-numeric identifier (e.g. a uuid) is fine
  return Number.isNaN(numeric) ? true : numeric > 0;
};

const isPartyRegistered = (
  party: ISettlementParty,
  requiredIdentifiers: PartyIdentifierKey[]
): boolean => {
  // an identifier the settlement request actually sends must be there and usable
  if (
    requiredIdentifiers.some(
      (key) => !isValidPartyIdentifier(party.identifiers[key])
    )
  ) {
    return false;
  }

  // otherwise the party is registered when it carries at least one identifier
  // and none of the identifiers it does carry is a placeholder (0 / null / "")
  const presentKeys = IDENTIFIER_KEYS.filter((key) =>
    isPresent(party.identifiers[key])
  );
  return (
    presentKeys.length > 0 &&
    presentKeys.every((key) => isValidPartyIdentifier(party.identifiers[key]))
  );
};

const getRoleParties = (
  contract: IContractDetailsResponseData,
  role: ESenderRole
): ISettlementParty[] => {
  const profiles = contract.partiesProfiles?.[role];

  if (profiles?.length) {
    return profiles.map((profile: Partial<UserProfile>) => ({
      role,
      name: profile.profile?.fullName,
      phoneNumber: profile.phoneNumber,
      identifiers: {
        id: profile.id,
        userId: profile.userId,
        userIdentification: profile.userIdentification,
      },
    }));
  }

  // Quick transactions have no `partiesProfiles`: their parties live on
  // `parties` and on `contract.customer` / `contract.provider`.
  const party = contract.parties?.find((p) => p.role === role);
  const user =
    role === ESenderRole.CUSTOMER
      ? contract.customer
      : role === ESenderRole.PROVIDER
      ? contract.provider
      : undefined;

  if (!party && !user) return [];

  return [
    {
      role,
      name: user?.profile?.fullName ?? party?.profile?.fullName,
      phoneNumber: user?.phoneNumber ?? party?.phoneNumber,
      identifiers: {
        id: user?.id,
        userId: party?.userId,
        userIdentification: user?.userIdentification,
      },
    },
  ];
};

/**
 * Parties of the contract that cannot be settled for because they are not
 * registered — they have no valid user id (zero / null / undefined).
 * @param requiredIdentifiers identifier keys the caller sends to the backend,
 * each of which must be present and valid on every party.
 */
export const getUnregisteredContractParties = (
  contract?: IContractDetailsResponseData | null,
  requiredIdentifiers: PartyIdentifierKey[] = []
): IUnregisteredParty[] => {
  if (!contract) return [];

  return SETTLEMENT_PARTY_ROLES.reduce<IUnregisteredParty[]>(
    (unregistered, role) => [
      ...unregistered,
      ...getRoleParties(contract, role)
        .filter((party) => !isPartyRegistered(party, requiredIdentifiers))
        .map(({ role: partyRole, name, phoneNumber }) => ({
          role: partyRole,
          name,
          phoneNumber,
        })),
    ],
    []
  );
};

export const areAllContractPartiesRegistered = (
  contract?: IContractDetailsResponseData | null,
  requiredIdentifiers: PartyIdentifierKey[] = []
): boolean =>
  getUnregisteredContractParties(contract, requiredIdentifiers).length === 0;
